"use client";

import { useId, type ReactNode } from "react";
import { naechsteSortierung, sortAnsage, type Sortierung } from "./tabellen-logik";
import {
  DatenZeile,
  KartenListe,
  KopfZelle,
  Seitenwahl,
  SkelettKarten,
  SkelettKoerper,
  TABELLEN_RAHMEN,
  ZustandsKarte,
  aktionenKopfKlasse,
  namensKopfKlasse,
  type SeitenAngabe,
  type ZustandAktion,
} from "./tabellen-teile";
import type { TabellenDichte, TabellenSpalte, TabellenZeile, TabellenZustand } from "./tabellen-typen";

export interface RegisterLeer {
  titel: string;
  text?: string;
  /** Nur mit Recht übergeben (r110). */
  aktion?: ZustandAktion;
}
export interface RegisterKeineTreffer {
  suchwort?: string;
  text?: string;
  onZuruecksetzen?: () => void;
}
export interface RegisterFehler {
  titel?: string;
  text?: string;
  onErneut?: () => void;
}

/**
 * Register A (Musterkatalog M-003, Design-Guide `tabellen.md`, T1–T4, T13, T15, T17): Karte mit Kopf
 * (Titel, Zähler, Aktionsslot, Werkzeug-Slot für Schnellfilter/Suche), Tabelle mit Sortierknopf im `th`
 * (`aria-sort`, kontrolliert: der Aufrufer sortiert), genau einem Haupt-Link je Zeile mit erweiterter Klickfläche,
 * Blättern im Fuß, Dichte Standard/Kompakt und den Zuständen leer / keine Treffer / lädt / Fehler.
 * Unter 768 px erscheinen die Zeilen als Karten (T12). Der Baustein sortiert, filtert und lädt nicht selbst;
 * Zeilen ohne Recht fehlen still (r110) – das entscheidet der Aufrufer mit derselben Prüfung wie die Action.
 */
export function RegisterTabelle({
  id,
  titel,
  beschriftung,
  anzahl,
  anzahlEinheit,
  aktion,
  titelSichtbar = true,
  werkzeuge,
  namensSpalte,
  spalten,
  zeilen,
  sortierung,
  onSortieren,
  dichte = "standard",
  seite,
  zustand = "bereit",
  leer,
  keineTreffer,
  fehler,
}: {
  id?: string;
  titel: string;
  /** Tabellenname für `<caption class="sr-only">`; Standard = `titel`. */
  beschriftung?: string;
  /** Zähler im Fuß („42 Einträge“, bei Seiten zusammen mit „Seite n von m“); zählt nur Sichtbares (r110). */
  anzahl?: number;
  /** Wort im Zähler statt „Eintrag/Einträge“ (additiv; Besprechungen P1, A6 N4: „n Besprechungen“). */
  anzahlEinheit?: { eins: string; mehr: string };
  /** Eine Primäraktion rechts im Kopf, nur mit Recht. */
  aktion?: ReactNode;
  /** `false`: die Kartenüberschrift (h2) steht nur für Hilfstechnik, weil der Seitentitel dasselbe Wort schon sichtbar trägt. */
  titelSichtbar?: boolean;
  /** Zweite Kopfzeile: Schnellfilter, Suche, Filter, Dichte-Wahl (Aufrufer). */
  werkzeuge?: ReactNode;
  namensSpalte: string;
  spalten: TabellenSpalte[];
  zeilen: TabellenZeile[];
  sortierung?: Sortierung;
  onSortieren?: (neu: Sortierung) => void;
  dichte?: TabellenDichte;
  seite?: SeitenAngabe;
  zustand?: TabellenZustand;
  leer?: RegisterLeer;
  keineTreffer?: RegisterKeineTreffer;
  fehler?: RegisterFehler;
}) {
  const auto = useId();
  const basis = id ?? auto;
  const titelId = `${basis}-titel`;
  const zaehlText =
    typeof anzahl === "number"
      ? anzahl === 1
        ? `1 ${anzahlEinheit?.eins ?? "Eintrag"}`
        : `${anzahl} ${anzahlEinheit?.mehr ?? "Einträge"}`
      : undefined;
  const mitAktionen = zeilen.some((z) => z.aktionen);
  const spaltenAnzahl = spalten.length + 1 + (mitAktionen ? 1 : 0);
  const effektiv: TabellenZustand = zustand === "bereit" && zeilen.length === 0 ? "leer" : zustand;
  const kopfSpalten = sortierung ? spalten.find((s) => s.id === sortierung.spalte) : undefined;
  const ansage = sortierung && kopfSpalten ? sortAnsage(kopfSpalten.kopf, sortierung.richtung) : "";

  const kopf = (
    <>
      <thead>
        <tr>
          <th scope="col" className={namensKopfKlasse(dichte)}>
            {namensSpalte}
          </th>
          {spalten.map((s) => (
            <KopfZelle
              key={s.id}
              spalte={s}
              dichte={dichte}
              sortierung={sortierung}
              onSortieren={(spalteId) => {
                if (onSortieren) onSortieren(naechsteSortierung(sortierung, spalteId));
              }}
            />
          ))}
          {mitAktionen && (
            <th scope="col" className={aktionenKopfKlasse(dichte)}>
              <span className="sr-only">Aktionen</span>
            </th>
          )}
        </tr>
      </thead>
    </>
  );

  let koerper: ReactNode;
  if (effektiv === "laedt") {
    koerper = (
      <>
        <div className="hidden md:block">
          <div className={TABELLEN_RAHMEN}>
            <table className="min-w-full" aria-busy="true">
              <caption className="sr-only">{beschriftung ?? titel}</caption>
              {kopf}
              <SkelettKoerper spaltenAnzahl={spaltenAnzahl} dichte={dichte} />
            </table>
          </div>
        </div>
        <SkelettKarten />
      </>
    );
  } else if (effektiv === "leer") {
    koerper = (
      <ZustandsKarte
        art="leer"
        titel={leer?.titel ?? "Noch keine Einträge"}
        text={leer ? leer.text : "Sobald es Einträge gibt, erscheinen sie hier."}
        aktion={leer?.aktion}
      />
    );
  } else if (effektiv === "keineTreffer") {
    koerper = (
      <ZustandsKarte
        art="keineTreffer"
        titel={keineTreffer?.suchwort ? `Keine Treffer für „${keineTreffer.suchwort}“` : "Keine Treffer"}
        text={keineTreffer?.text ?? "Mit den gewählten Filtern passt kein Eintrag."}
        aktion={keineTreffer?.onZuruecksetzen ? { text: "Filter zurücksetzen", onKlick: keineTreffer.onZuruecksetzen } : undefined}
      />
    );
  } else if (effektiv === "fehler") {
    koerper = (
      <ZustandsKarte
        art="fehler"
        titel={fehler?.titel ?? "Die Einträge konnten nicht geladen werden."}
        text={fehler?.text ?? "Bitte erneut versuchen."}
        aktion={fehler?.onErneut ? { text: "Erneut laden", onKlick: fehler.onErneut } : undefined}
      />
    );
  } else {
    koerper = (
      <>
        <div className="hidden md:block">
          <div className={TABELLEN_RAHMEN}>
            <table className="min-w-full">
              <caption className="sr-only">{beschriftung ?? titel}</caption>
              {kopf}
              <tbody className="divide-y divide-table-line">
                {zeilen.map((z) => (
                  <DatenZeile key={z.id} zeile={z} spalten={spalten} dichte={dichte} mitAktionen={mitAktionen} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <KartenListe zeilen={zeilen} spalten={spalten} />
        {seite ? (
          <Seitenwahl {...seite} anzahlText={zaehlText} />
        ) : (
          zaehlText && (
            <div className="px-4 py-3 border-t border-table-line">
              <p className="text-sm text-muted-foreground-1">{zaehlText}</p>
            </div>
          )
        )}
      </>
    );
  }

  return (
    <>
      {/* Preline: component/tables/tables-rounded-tables (Hülle, Kopf, Sortier-Kopf und Fuß nach data-display/data-display-sub-tables-records/data-display-cat-project-tables/project-table-with-search-and-filters) */}
      <section id={id} aria-labelledby={titelId} className="bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden">
        {/* `titelSichtbar={false}` (PM-A6): steht die Überschrift schon als Seitentitel direkt darüber, bleibt sie nur für Hilfstechnik (sr-only). */}
        {(titelSichtbar || aktion) && (
          <div className="p-4 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0 flex items-center gap-x-3">
              {titelSichtbar && (
                <h2 id={titelId} className="text-lg font-semibold text-foreground">
                  {titel}
                </h2>
              )}
            </div>
            {aktion && <div className="flex items-center gap-x-2">{aktion}</div>}
          </div>
        )}
        {!titelSichtbar && (
          <h2 id={titelId} className="sr-only">
            {titel}
          </h2>
        )}
        {/* Abweichung AW-101 (6): Ohne sichtbaren Kopf (Titel verborgen, keine Aktion) ist die Werkzeugzeile die erste Zeile der Karte: sie braucht dann selbst den oberen Innenabstand (Live-Bild I4: Chips saßen an der Kartenkante). */}
        {werkzeuge && <div className={titelSichtbar || aktion ? "px-4 pb-3 flex flex-wrap items-center gap-2" : "px-4 pt-4 pb-3 flex flex-wrap items-center gap-2"}>{werkzeuge}</div>}
        <div role="status" className="sr-only">
          {ansage}
        </div>
        {koerper}
      </section>
      {/* End Register */}
    </>
  );
}
